<template>
  <div
    class="flex grow flex-col overflow-y-auto border-r border-background-fg/8 bg-background pb-4 pt-5"
  >
    <div
      class="flex shrink-0 items-center justify-center px-4"
    >
      <IconBrandLogo class="h-8 w-auto" />
    </div>

    <TheSidebarTransactionCounts
      v-if="permissions.includes('view_transaction_count')"
    />

    <div class="mt-5 flex grow flex-col">
      <nav
        class="flex flex-1 flex-col space-y-2 bg-background px-2"
        aria-label="Sidebar"
      >
        <template
          v-for="(section, sectionIndex) in topSections"
          :key="sectionIndex"
        >
          <template v-if="!('children' in section)">
            <SidebarItem :route="section" />
          </template>
          <template v-else>
            <RouterLink
              #default="{ isActive, route: { meta } }"
              custom
              :to="section"
            >
              <div
                :class="[
                  'space-y-1 border-l-2',
                  isActive
                    ? 'border-primary bg-background-fg/3 text-background-fg/87'
                    : 'border-transparent bg-white text-background-fg/70',
                ]"
              >
                <p
                  :class="[
                    'group flex w-full items-center rounded-md py-2 pl-2 pr-1 text-left text-xs font-medium',
                  ]"
                >
                  <span class="flex-1">{{
                    meta.pageTitle
                  }}</span>
                </p>
                <div class="space-y-1">
                  <template
                    v-for="(
                      child, childIndex
                    ) in section.children"
                    :key="childIndex"
                  >
                    <SidebarItem :route="child" />
                  </template>
                </div>
              </div>
            </RouterLink>
          </template>
        </template>
        <div class="flex-1"></div>
        <template
          v-for="(section, sectionIndex) in bottomSections"
          :key="sectionIndex"
        >
          <SidebarItem :route="section" />
        </template>
        <template v-if="currentUser">
          <div class="border-t border-background-fg/20">
            <div class="flex items-center px-4 pb-2 pt-4">
              <div
                class="text-sm font-medium text-background-fg/80"
              >
                {{ currentUser.email }}
              </div>
            </div>
            <RouterLink
              #default="{ navigate, href }"
              custom
              :to="{ name: 'userPasswordChange' }"
            >
              <div class="relative">
                <a
                  class="block w-full rounded-md px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
                  :href="href"
                  @click="navigate"
                >
                  {{ changePasswordText }}
                </a>
                <template v-if="shouldAlertPassword">
                  <IconExclamationCircleFilled
                    class="absolute right-2 top-1/2 size-4 -translate-y-1/2 text-error"
                  />
                </template>
              </div>
            </RouterLink>
            <div class="space-y-1">
              <button
                class="block w-full rounded-md px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
                @click="onLogOut"
              >
                Log Out
              </button>
            </div>
          </div>
        </template>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SidebarRoute } from "@/components/SidebarItem.vue"
import type { RouteLocationAsRelative } from "vue-router"

export type SidebarSection =
  | SidebarRoute
  | (RouteLocationAsRelative & {
      children: Array<SidebarRoute>
    })

const props = defineProps<{
  sections: Array<SidebarSection>
}>()
const { permissions } = useAuth()

const { currentUser } = useCurrentUser()

const topSections = computed(() =>
  props.sections.filter(
    (section) =>
      !("showAtBottom" in section) ||
      ("showAtBottom" in section && !section.showAtBottom)
  )
)

const bottomSections = computed<Array<SidebarRoute>>(() =>
  props.sections.filter(
    (section) =>
      "showAtBottom" in section && section.showAtBottom
  )
)

const onLogOut = async () => {
  await logOut()
}

const shouldAlertPassword = computed(
  () => currentUser.value?.hasPassword === false
)

const changePasswordText = computed(() =>
  currentUser.value?.hasPassword
    ? "Change Password"
    : "Set Password"
)
</script>
